import { Component, signal, computed, ElementRef, ViewChildren, ViewChild, QueryList, inject, OnInit, input, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { ProfessionalsWizardService } from '../../../services/professionals-wizard.service';
import { ProfessionalsService } from '../../../../core/services/professionals.service';
import { ProfessionalsDataService } from '../../../services/professionals-data.service';
import { UserSessionService } from '../../../../core/services/mock-auth.service';
import { ComponentCanDeactivate } from '../../../../core/guards/unsaved-changes.guard';
import { ConfirmationDialogService } from '../../../../core/services/confirmation-dialog.service';
import { RegistrationStepService, RegistrationStepResponse, RegistrationStatus, RegistrationStep } from '../../../../core/services/registration-step.service';
import { hasMeaningfulText, isMissingMeaningfulText } from '../../../../core/validators/text-value.utils';
import { forkJoin, Observable } from 'rxjs';
import { ValidationErrorSection } from '../../validation-error-summary/validation-error-summary.model';
import { ValidationErrorSummaryComponent } from '../../validation-error-summary/validation-error-summary.component';
import { CollaboratorsSubsectionComponent } from './collaborators-subsection.component';
import { getCollaboratorTypeConfig } from './collaborator-type-config';

interface Collaborator {
  id: number;
  nome: string;
  cognome: string;
  codiceFiscale: string;
  codiceFiscaleError: string;
  ruolo: string;
  necessitaAccesso: boolean;
  expanded: boolean;
  // Flag "touched": l'errore rosso di un campo obbligatorio compare solo dopo che il
  // campo ha perso il focus (blur) o dopo il click su "Salva e prosegui", non appena
  // la scheda viene aggiunta.
  nomeTouched: boolean;
  cognomeTouched: boolean;
  codiceFiscaleTouched: boolean;
}

@Component({
  selector: 'app-professionals-collaborators',
  standalone: true,
  providers: [MessageService],
  imports: [CommonModule, FormsModule, ButtonModule, InputTextModule, CheckboxModule, RadioButtonModule, ToastModule, TooltipModule, ValidationErrorSummaryComponent, CollaboratorsSubsectionComponent],
  templateUrl: './professionals-collaborators.component.html',
  styleUrl: './professionals-collaborators.component.scss',
})
export class ProfessionalsCollaboratorsComponent implements OnInit, ComponentCanDeactivate {
  @ViewChildren('firstInput') firstInputs!: QueryList<ElementRef>;
  @ViewChild('collabSub') collabSub?: CollaboratorsSubsectionComponent;
  readonly hasMeaningfulText = hasMeaningfulText;
  readonly isMissingMeaningfulText = isMissingMeaningfulText;

  // Optional input for professional ID (used in review mode)
  professionalIdInput = input<string | null>(null);
  
  // Optional input to force readonly mode (for review/detail pages)
  readonly = input<boolean>(false);

  private professionalsWizardService = inject(ProfessionalsWizardService);
  private professionalsService = inject(ProfessionalsService);
  private professionalsDataService = inject(ProfessionalsDataService);
  private mockAuthService = inject(UserSessionService);
  private messageService = inject(MessageService);
  private registrationStepService = inject(RegistrationStepService);
  private confirmDialogService = inject(ConfirmationDialogService);
  private router = inject(Router);
  private readonly UNSAVED_CHANGES_MESSAGE = 'Se cambi sezione o lasci la pagina senza cliccare su "Salva", perderai i dati inseriti. Vuoi procedere?';
  private allowNextNavigation = false;

  // Collaborators list
  collaborators = signal<Collaborator[]>([]);

  // Counter for unique IDs
  private collaboratorIdCounter = 1;

  // Live region message for screen readers
  liveRegionMessage = signal<string>('');
  validationErrorSections = signal<ValidationErrorSection[]>([]);

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.shouldWarnBeforeLeaving()) {
      event.preventDefault();
      event.returnValue = true;
    }
  }

  // Current registration step (for detecting modification requests)
  currentRegistrationStep = signal<RegistrationStepResponse | null>(null);
  
  // Step history (to check for MODIFICATION_REQUESTED)
  stepHistory = signal<RegistrationStepResponse[]>([]);

  // Computed: Check if there's a modification request
  hasModificationRequest = computed(() => {
    const step = this.currentRegistrationStep();
    const history = this.stepHistory();
    
    if (!step || this.professionalIdInput()) return false; // Don't show in review mode
    
    // Only show banner if:
    // 1. Current step is DATA_INPUT with USER_INPUT status
    // 2. There's a VERIFICATION step with MODIFICATION_REQUESTED in history
    if (step.step === 'DATA_INPUT' && step.status === RegistrationStatus.USER_INPUT) {
      return history.some(s => 
        s.step === 'VERIFICATION' && 
        s.status === RegistrationStatus.MODIFICATION_REQUESTED
      );
    }
    
    return false;
  });
  
  // Computed: Check if all fields should be disabled (readonly mode or review mode)
  isFieldsDisabled = computed(() => {
    return this.professionalsDataService.isReadOnly() || this.readonly();
  });
  
  // Alias per consistency con gli altri componenti
  isReadOnly = this.isFieldsDisabled;

  professionalSummaryBusinessName = computed(() => {
    const generalData = this.professionalsWizardService.cachedGeneralData();
    return generalData?.companyData?.businessName || generalData?.businessName || this.businessNameFromApi();
  });

  private businessNameFromApi = signal<string>('');
  private professionalTypeFromApi = signal<string>('');

  professionalIdDisplay = computed(() => {
    return this.professionalIdInput() || this.getCurrentProfessionalId() || '';
  });

  professionalSummaryType = computed(() => {
    const cachedType = this.professionalsWizardService.cachedGeneralData()?.professionalType;
    const tipologia = (this.professionalTypeFromApi() || cachedType || '').trim().toUpperCase();
    const normalizedTypes: Record<string, string> = {
      AUTO_EXPERT: 'Perito Auto',
      RE_EXPERT: 'Perito RE',
      INVESTIGATOR: 'Investigatore',
      DOCTOR: 'Medico Legale',
      LAWYER: 'Legale',
      BODYSHOP: 'Carrozzeria',
      NETWORK: 'Fornitore Rete',
      HAIL_PROVIDER: 'Fornitore Grandine',
    };

    return normalizedTypes[tipologia] || tipologia;
  });

  /**
   * Raw professional type (e.g. AUTO_EXPERT) used to drive the per-type
   * behaviour of the "Collaboratori" subsection.
   */
  collaboratorProfessionalType = computed(() => {
    const cachedType = this.professionalsWizardService.cachedGeneralData()?.professionalType;
    return (this.professionalTypeFromApi() || cachedType || '').trim().toUpperCase();
  });

  // Computed: Get modification comment from VERIFICATION MODIFICATION_REQUESTED step
  modificationComment = computed(() => {
    const history = this.stepHistory();
    
    // Find the most recent VERIFICATION with MODIFICATION_REQUESTED
    const modificationSteps = history
      .filter(s => s.step === 'VERIFICATION' && s.status === RegistrationStatus.MODIFICATION_REQUESTED)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    
    if (modificationSteps.length > 0) {
      // Parse "Modification requested: actual message" format
      const comment = modificationSteps[0].comments || '';
      return comment.replace(/^Modification requested:\s*/, '').trim();
    }
    
    return null;
  });

  // Signal for expand/collapse state of modification notes
  isModificationNotesExpanded = signal<boolean>(false);
  private readonly MAX_COMMENT_LENGTH = 150;

  // Computed: Check if comment is long
  isCommentLong = computed(() => {
    const comment = this.modificationComment();
    return comment ? comment.length > this.MAX_COMMENT_LENGTH : false;
  });

  // Computed: Get truncated comment
  displayedComment = computed(() => {
    const comment = this.modificationComment();
    if (!comment) return null;
    
    if (this.isCommentLong() && !this.isModificationNotesExpanded()) {
      return comment.substring(0, this.MAX_COMMENT_LENGTH) + '...';
    }
    
    return comment;
  });

  // Toggle modification notes expansion
  toggleModificationNotes(): void {
    this.isModificationNotesExpanded.update(expanded => !expanded);
  }

  // Computed: verifica se lo step corrente è completato
  isStepCompleted = computed(() => {
    const currentStep = this.professionalsWizardService.steps().find(step => step.id === 3);
    return currentStep?.completed ?? false;
  });

  // In questo step del wizard l'unico ruolo possibile è "Impiegato amministrativo":
  // il campo non viene più mostrato (nessuna scelta), ma il valore viene comunque
  // valorizzato in modo fisso nel payload di salvataggio.
  private readonly IMPIEGATO_AMMINISTRATIVO_ROLE = 'impiegato_amministrativo';

  ngOnInit() {
    // Set this step as active in the wizard
    this.professionalsWizardService.setStep(3);
    
    // Load existing collaborators if any
    this.loadExistingCollaborators();
    
    // Load completion status for previous steps
    const profId = this.getCurrentProfessionalId();
    if (profId) {
      // Load current registration step from backend (needed for read-only mode detection)
      this.professionalsWizardService.loadCurrentStep(profId);
      
      // Load both current step AND history to detect modification requests
      forkJoin({
        currentStep: this.registrationStepService.getCurrentStep(profId),
        history: this.registrationStepService.getStepHistory(profId)
      }).subscribe({
        next: ({ currentStep, history }) => {
          this.currentRegistrationStep.set(currentStep);
          this.stepHistory.set(history || []);
          console.log('📋 [Collaborators] Current registration step loaded:', currentStep);
          console.log('📋 [Collaborators] Step history loaded:', history?.length || 0, 'steps');
          
          // Check if there's a modification request in history
          if (currentStep.step === 'DATA_INPUT' && 
              currentStep.status === RegistrationStatus.USER_INPUT) {
            const hasModification = history?.some(s => 
              s.step === 'VERIFICATION' && 
              s.status === RegistrationStatus.MODIFICATION_REQUESTED
            );
            
            if (hasModification) {
              console.log('⚠️ [Collaborators] Modification request detected!');
              this.liveRegionMessage.set('Richiesta di modifica presente. Si prega di leggere i commenti del revisore.');
            }
          }
        },
        error: (err) => {
          console.error('❌ Error loading registration step or history:', err);
        }
      });
      
      // Use the same pattern as questionnaire-attachments
      this.professionalsWizardService.preloadGeneralDataStatus(profId);
      
      const tipologia = this.professionalsDataService.getTipologiaFiduciario();
      if (tipologia) {
        this.professionalsWizardService.preloadSpecificDataStatus(profId, tipologia);
      }
      
      // NOTE: do NOT call preloadQuestionnaireStatus here — step 2 completion
      // was already set correctly by the questionnaire-attachments component.
      // Re-fetching from the backend can return a non-COMPLETED questionnaire-step
      // status (e.g. if only checklist items were uploaded, not the questionnaire
      // registration step) and would incorrectly override the completed state.
    }
  }

  canDeactivate(): Observable<boolean> | boolean {
    if (this.professionalsWizardService.consumeNavigationWithoutWarning()) {
      return true;
    }

    if (this.allowNextNavigation) {
      this.allowNextNavigation = false;
      return true;
    }

    if (!this.shouldWarnBeforeLeaving()) {
      return true;
    }

    return new Observable<boolean>((observer) => {
      this.confirmDialogService.confirmAction(
        this.UNSAVED_CHANGES_MESSAGE,
        () => {
          observer.next(true);
          observer.complete();
        },
        'Attenzione',
        'Esci senza salvare',
        'Resta nella pagina'
      );
    });
  }

  private shouldWarnBeforeLeaving(): boolean {
    return !this.isReadOnly();
  }

  /**
   * Load existing collaborators from the API
   */
  private loadExistingCollaborators(): void {
    const professionalId = this.getCurrentProfessionalId();
    console.log('📥 Loading collaborators for professional:', professionalId);
    
    if (!professionalId) {
      console.warn('⚠️ No professional ID found - skipping collaborators load');
      return;
    }

    this.professionalsService.getAssociatedUsers(professionalId).subscribe({
      next: (response) => {
        console.log('✅ Collaborators API response:', response);
        
        // Response has wrapper: { status: true, message: "...", data: [...] }
        const collaboratorsData = response?.data || [];
        
        if (Array.isArray(collaboratorsData) && collaboratorsData.length > 0) {
          // Map API response to internal model
          const loadedCollaborators = collaboratorsData.map((user: any, index: number) => ({
            id: index + 1,
            nome: user.firstName || '',
            cognome: user.lastName || '',
            codiceFiscale: user.fiscalCode || '',
            codiceFiscaleError: '',
            ruolo: user.role || this.IMPIEGATO_AMMINISTRATIVO_ROLE,
            necessitaAccesso: true,
            expanded: false,
            nomeTouched: false,
            cognomeTouched: false,
            codiceFiscaleTouched: false,
          }));
          
          this.collaborators.set(loadedCollaborators);
          this.collaboratorIdCounter = loadedCollaborators.length + 1;
          
          console.log('✅ Loaded', loadedCollaborators.length, 'existing collaborators');
        } else {
          console.log('ℹ️ No existing collaborators found (empty data array)');
        }
      },
      error: (error) => {
        console.log('⚠️ Error loading collaborators:', error.status, error.message);
        // If 404 or no data, it's ok - no collaborators exist yet
        if (error.status !== 404) {
          console.error('❌ Unexpected error loading collaborators:', error);
        }
      }
    });
  }

  /**
   * Validates Italian Codice Fiscale format
   * Supports both individual (16 alphanumeric) and company (11 numeric) formats
   */
  validateCodiceFiscale(value: string): string {
    if (!value) {
      return 'Il codice fiscale è obbligatorio';
    }

    const trimmedValue = value.trim().toUpperCase();

    // Individual format: 16 alphanumeric characters
    const individualPattern = /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/;
    // Company format: 11 numeric characters
    const companyPattern = /^[0-9]{11}$/;

    if (individualPattern.test(trimmedValue)) {
      return '';
    }

    if (companyPattern.test(trimmedValue)) {
      return '';
    }

    if (trimmedValue.length !== 16 && trimmedValue.length !== 11) {
      return 'Il codice fiscale deve essere di 16 caratteri (persone fisiche) o 11 cifre (aziende)';
    }

    return 'Formato codice fiscale non valido';
  }

  /**
   * Handles Codice Fiscale change
   */
  /**
   * Validates codice fiscale (updates error only, not the value)
   */
  onCodiceFiscaleChange(collaboratorId: number, value: string): void {
    // Aggiorna il valore; l'errore viene (ri)calcolato solo se il campo è già stato
    // "toccato" (blur o salvataggio), così non compare in rosso mentre si digita.
    this.collaborators.update((curr) =>
      curr.map((c) =>
        c.id === collaboratorId
          ? {
              ...c,
              codiceFiscale: value,
              codiceFiscaleError: c.codiceFiscaleTouched ? this.validateCodiceFiscale(value) : c.codiceFiscaleError,
            }
          : c,
      ),
    );
  }

  /**
   * Segna un campo obbligatorio come "toccato" al blur, così l'eventuale errore rosso
   * compare solo dopo che l'utente ha lasciato il campo (e non appena aggiunge la scheda).
   */
  markImpiegatoFieldTouched(collaboratorId: number, field: 'nome' | 'cognome' | 'codiceFiscale'): void {
    this.collaborators.update((curr) =>
      curr.map((c) => {
        if (c.id !== collaboratorId) return c;
        const updated: Collaborator = { ...c };
        if (field === 'nome') {
          updated.nomeTouched = true;
        } else if (field === 'cognome') {
          updated.cognomeTouched = true;
        } else {
          updated.codiceFiscaleTouched = true;
          updated.codiceFiscaleError = this.validateCodiceFiscale(updated.codiceFiscale);
        }
        return updated;
      }),
    );
  }

  /**
   * Adds a new collaborator
   */
  addCollaborator(): void {
    const newCollaborator: Collaborator = {
      id: this.collaboratorIdCounter++,
      nome: '',
      cognome: '',
      codiceFiscale: '',
      codiceFiscaleError: '',
      ruolo: this.IMPIEGATO_AMMINISTRATIVO_ROLE,
      necessitaAccesso: true,
      expanded: true,
      nomeTouched: false,
      cognomeTouched: false,
      codiceFiscaleTouched: false,
    };

    this.collaborators.update((current) => [...current, newCollaborator]);

    // Announce to screen readers
    this.liveRegionMessage.set(`Collaboratore ${this.collaborators().length} aggiunto`);
    setTimeout(() => this.liveRegionMessage.set(''), 1000);

    // Focus management: Move focus to the new collaborator's first input
    setTimeout(() => {
      const inputs = this.firstInputs.toArray();
      const newInput = inputs[inputs.length - 1];
      if (newInput) {
        newInput.nativeElement.focus();
      }
    }, 100);
  }

  /**
   * Toggles the collapsed/expanded state of an impiegato amministrativo card
   */
  toggleImpiegatoExpanded(id: number): void {
    this.collaborators.update((current) =>
      current.map((c) => (c.id === id ? { ...c, expanded: !c.expanded } : c)),
    );
  }

  /** Collapsed summary shown in the card header: "cognome · nome". */
  collapsedImpiegatoTitle(collaborator: Collaborator): string {
    return [collaborator.cognome, collaborator.nome]
      .map((p) => (p || '').trim())
      .filter((p) => p.length > 0)
      .join(' · ');
  }

  /**
   * Removes a collaborator
   */
  removeCollaborator(id: number): void {
    const index = this.collaborators().findIndex((c) => c.id === id);
    this.collaborators.update((current) => current.filter((c) => c.id !== id));

    // Announce to screen readers
    this.liveRegionMessage.set(`Collaboratore ${index + 1} rimosso`);
    setTimeout(() => this.liveRegionMessage.set(''), 1000);

    // Focus management: Move focus to the previous collaborator or the add button
    setTimeout(() => {
      const inputs = this.firstInputs.toArray();
      if (inputs.length > 0) {
        const targetIndex = Math.max(0, index - 1);
        const targetInput = inputs[targetIndex];
        if (targetInput) {
          targetInput.nativeElement.focus();
        }
      } else {
        // If no collaborators left, focus on "Aggiungi Collaboratore" button
        const addButton = document.querySelector('[aria-label*="Aggiungi collaboratore"]') as HTMLElement;
        if (addButton) {
          addButton.focus();
        }
      }
    }, 100);
  }

  /**
   * Validates all collaborators
   */
  validateAllCollaborators(): boolean {
    let isValid = true;
    const validationSections: ValidationErrorSection[] = [];
    const updatedCollaborators = this.collaborators().map((collaborator, index) => {
      const codiceFiscaleError = this.validateCodiceFiscale(collaborator.codiceFiscale);
      const errors = [];

      // Validate required fields
      if (this.isMissingMeaningfulText(collaborator.nome)) {
        isValid = false;
        errors.push({ field: 'Nome', fieldId: `nome-${collaborator.id}` });
      }
      if (this.isMissingMeaningfulText(collaborator.cognome)) {
        isValid = false;
        errors.push({ field: 'Cognome', fieldId: `cognome-${collaborator.id}` });
      }
      if (codiceFiscaleError) {
        isValid = false;
        errors.push({ field: 'Codice Fiscale', message: codiceFiscaleError, fieldId: `cf-${collaborator.id}` });
      }

      if (errors.length > 0) {
        validationSections.push({ title: `Impiegato amministrativo #${index + 1}`, errors });
      }

      // Al salvataggio tutti i campi diventano "touched": gli eventuali errori
      // obbligatori vengono così evidenziati anche se il campo non ha mai avuto il focus.
      return {
        ...collaborator,
        codiceFiscaleError,
        nomeTouched: true,
        cognomeTouched: true,
        codiceFiscaleTouched: true,
      };
    });

    this.collaborators.set(updatedCollaborators);
    this.validationErrorSections.set(validationSections);
    
    return isValid;
  }

  /**
   * Builds the associated-users (impiegati amministrativi) payload from the local rows.
   */
  private buildImpiegatiPayload() {
    return this.collaborators().map(collab => ({
      firstName: collab.nome,
      lastName: collab.cognome,
      fiscalCode: collab.codiceFiscale,
      // Ruolo non più selezionabile a video: passato fisso come "impiegato amministrativo".
      role: this.IMPIEGATO_AMMINISTRATIVO_ROLE,
      needsClaimsPortalAccess: true,
      // Pivot v2 (Codici SOL / MFA): la mail di accesso ai sistemi AXA non viene
      // più raccolta in anagrafica, ma nella fase MFA per associazione SOL ↔ persona.
      personalEmail: null,
    }));
  }

  /** Whether the "Collaboratori" subsection is hidden for the current professional type. */
  isCollaboratorsSubsectionHidden(): boolean {
    return getCollaboratorTypeConfig(this.collaboratorProfessionalType()).hidden;
  }

  /**
   * D12 – Fiscal-code uniqueness across BOTH lists (impiegati + collaboratori).
   * Returns an error message when a fiscal code is duplicated within a single
   * list or shared between the two lists, otherwise null.
   */
  private checkFiscalCodeUniqueness(): string | null {
    const normalize = (cf: string) => (cf || '').trim().toUpperCase();
    const impiegatiCfs = this.collaborators().map(c => normalize(c.codiceFiscale)).filter(Boolean);
    const collabCfs = (this.collabSub && !this.isCollaboratorsSubsectionHidden())
      ? this.collabSub.getFiscalCodes().map(normalize).filter(Boolean)
      : [];

    // Intra-list duplicates
    const firstDup = (list: string[]): string | null => {
      const seen = new Set<string>();
      for (const cf of list) {
        if (seen.has(cf)) return cf;
        seen.add(cf);
      }
      return null;
    };
    const dupImpiegati = firstDup(impiegatiCfs);
    if (dupImpiegati) {
      return `Il codice fiscale ${dupImpiegati} è ripetuto tra gli impiegati amministrativi.`;
    }
    const dupCollab = firstDup(collabCfs);
    if (dupCollab) {
      return `Il codice fiscale ${dupCollab} è ripetuto tra i collaboratori professionisti.`;
    }

    // Cross-list duplicates
    const impiegatiSet = new Set(impiegatiCfs);
    const crossDup = collabCfs.find(cf => impiegatiSet.has(cf));
    if (crossDup) {
      return `Il codice fiscale ${crossDup} è utilizzato sia tra gli impiegati amministrativi sia tra i collaboratori professionisti.`;
    }
    return null;
  }

  /**
   * "Salva bozza" – persists both subsections WITHOUT validation and marks the
   * staff section as DRAFT (step remains not completed, no navigation).
   */
  onSalvaBozza(): void {
    const professionalId = this.getCurrentProfessionalId();
    if (!professionalId) {
      this.messageService.add({ severity: 'error', summary: 'Errore', detail: 'ID fiduciario non trovato. Riprova.', life: 5000 });
      return;
    }
    const duplicates = (this.collabSub && !this.isCollaboratorsSubsectionHidden())
      ? this.collabSub.validateClaimsPhoneDuplicates()
      : { valid: true, sections: [] as ValidationErrorSection[] };
    this.validationErrorSections.set(duplicates.sections);
    if (!duplicates.valid) {
      this.liveRegionMessage.set('Numeri duplicati. Correggi i campi evidenziati.');
      return;
    }
    this.persistBoth(professionalId, 'DRAFT', false);
  }

  /**
   * "Salva e prosegui" – validates both subsections (riga sempre completa),
   * enforces fiscal-code uniqueness, optionally confirms empty subsections,
   * then persists both, marks the staff section as FILLED and navigates forward.
   */
  onSalvaEProsegui(): void {
    const impiegatiValid = this.validateAllCollaborators();
    const collabResult = (this.collabSub && !this.isCollaboratorsSubsectionHidden())
      ? this.collabSub.validateFilled()
      : { valid: true, sections: [] as ValidationErrorSection[] };

    const impiegatiSections = this.validationErrorSections();
    this.validationErrorSections.set([...impiegatiSections, ...collabResult.sections]);

    if (!impiegatiValid || !collabResult.valid) {
      this.liveRegionMessage.set('Errori di validazione. Correggi i campi evidenziati.');
      return;
    }

    // D12 – fiscal code uniqueness across both lists
    const cfError = this.checkFiscalCodeUniqueness();
    if (cfError) {
      this.messageService.add({ severity: 'warn', summary: 'Codice Fiscale Duplicato', detail: cfError, life: 7000 });
      this.liveRegionMessage.set('Errore: codice fiscale duplicato');
      return;
    }

    const professionalId = this.getCurrentProfessionalId();
    if (!professionalId) {
      this.messageService.add({ severity: 'error', summary: 'Errore', detail: 'ID fiduciario non trovato. Riprova.', life: 5000 });
      return;
    }

    // Empty subsection confirmation
    const impiegatiEmpty = this.collaborators().length === 0;
    const collabHidden = this.isCollaboratorsSubsectionHidden();
    const collabEmpty = collabHidden ? false : (this.collabSub ? this.collabSub.isEmpty() : true);

    let confirmMessage: string | null = null;
    if (impiegatiEmpty && collabEmpty) {
      confirmMessage = 'Non hai inserito né impiegati amministrativi né collaboratori professionisti. Vuoi confermare e proseguire?';
    } else if (impiegatiEmpty) {
      confirmMessage = 'Non hai inserito impiegati amministrativi. Vuoi confermare e proseguire?';
    } else if (collabEmpty) {
      confirmMessage = 'Non hai inserito collaboratori professionisti. Vuoi confermare e proseguire?';
    }

    if (confirmMessage) {
      this.confirmDialogService.confirmAction(
        confirmMessage,
        () => this.persistBoth(professionalId, 'FILLED', true),
        'Conferma',
        'Conferma e prosegui',
        'Annulla'
      );
      return;
    }

    this.persistBoth(professionalId, 'FILLED', true);
  }

  /**
   * Persists both subsections (impiegati amministrativi + collaboratori) and then
   * updates the staff section status. On FILLED it marks the wizard step completed
   * and optionally navigates to the next visible step.
   */
  private persistBoth(professionalId: string, status: 'DRAFT' | 'FILLED', navigateAfter: boolean): void {
    const impiegatiPayload = this.buildImpiegatiPayload();
    const collabPayload = (this.collabSub && !this.isCollaboratorsSubsectionHidden())
      ? this.collabSub.buildPayload()
      : [];

    forkJoin([
      this.professionalsService.saveAssociatedUsers(professionalId, impiegatiPayload),
      this.professionalsService.saveCollaborators(professionalId, collabPayload),
    ]).subscribe({
      next: () => {
        this.professionalsService.setStaffSectionStatus(professionalId, status).subscribe({
          next: () => {
            if (status === 'FILLED') {
              this.professionalsWizardService.markStepCompleted(3, true);
              this.professionalsWizardService.saveState();
              this.liveRegionMessage.set('Collaboratori professionisti e impiegati amministrativi salvati con successo');
              this.messageService.add({ severity: 'success', summary: 'Successo', detail: 'Dati salvati con successo.', life: 3000 });
              if (navigateAfter) {
                const nextStep = this.professionalsWizardService.getNextVisibleStep(3);
                if (nextStep?.routerLink) {
                  this.allowNextNavigation = true;
                  this.professionalsWizardService.allowNextNavigationWithoutWarning();
                  this.router.navigate([nextStep.routerLink]).then(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
                }
              }
            } else {
              this.professionalsWizardService.markStepCompleted(3, false);
              this.professionalsWizardService.saveState();
              this.liveRegionMessage.set('Bozza salvata con successo');
              this.messageService.add({ severity: 'success', summary: 'Bozza salvata', detail: 'Bozza salvata con successo.', life: 3000 });
              this.allowNextNavigation = false;
            }
          },
          error: (error) => this.handleSaveError(error),
        });
      },
      error: (error) => this.handleSaveError(error),
    });
  }

  /** Shared error handling for the staff-section save flow. */
  private handleSaveError(error: unknown): void {
    console.error('Errore durante il salvataggio della sezione impiegati/collaboratori:', error);
    const err = error as { error?: { errorCode?: string; message?: string } };
    if (err?.error?.errorCode === 'DUPLICATE_RESOURCE') {
      const message = err?.error?.message || '';
      const errorDetail = message.toLowerCase().includes('email')
        ? 'Un indirizzo email inserito è già registrato nel sistema. Verifica gli indirizzi email.'
        : (message || 'Esiste già un anagrafica con gli stessi dati.');
      this.messageService.add({ severity: 'warn', summary: 'Dato Duplicato', detail: errorDetail, life: 7000 });
      this.liveRegionMessage.set('Errore: dato duplicato rilevato');
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: err?.error?.message || 'Impossibile salvare i dati. Riprova più tardi.',
        life: 5000,
      });
      this.liveRegionMessage.set('Errore durante il salvataggio');
    }
  }

  /**
   * Gets the current professional ID from input or mockAuthService
   */
  private getCurrentProfessionalId(): string | null {
    return this.professionalIdInput() || this.mockAuthService.getDemoProfessionalId();
  }
}
